// -------------------------------
// TAX REGIME INTERFACES
// -------------------------------
export interface PendingTaxRegime {
    RegimeName?: string;
    FinancialYearID?: number;
    IsDefault?: boolean;
    AllowsStandardDeduction?: boolean;
    StandardDeductionAmount?: number;
    AllowsChapterVI_ADeductions?: boolean;
    RebateUnderSection87A_MaxIncome?: number;
    RebateUnderSection87A_Amount?: number;
    Status?: string; // active | inactive
}

export interface PendingDeleteTaxRegime {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE TAX REGIME — START
// -------------------------------
export const startCreateTaxRegimeFlow = (
    setPending: (data: PendingTaxRegime | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ RegimeName: nameFromIntent });

        pushAIChat(`Great! Which financial year should "${nameFromIntent}" belong to?`);
        speak(`Which financial year should ${nameFromIntent} belong to?`);

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "financial-year" }
            })
        );
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the tax regime be called?");
    speak("What should the tax regime be called?");
};

// -------------------------------
// CREATE TAX REGIME — PROCESS
// -------------------------------
export const processPendingTaxRegime = (
    text: string,
    pending: PendingTaxRegime,
    setPending: (data: PendingTaxRegime | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — REGIME NAME
    if (!pending.RegimeName) {
        const name = text.trim();

        if (!name) {
            pushAIChat("Please provide a valid tax regime name.");
            speak("Please provide a valid tax regime name.");
            return;
        }

        setPending({ ...pending, RegimeName: name });

        pushAIChat("Which financial year does this regime belong to?");
        speak("Which financial year does this regime belong to?");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "financial-year" }
            })
        );
        return;
    }

    // STEP 2 — FINANCIAL YEAR (handled via suggestion click)
    if (!pending.FinancialYearID) {
        pushAIChat("Please select a financial year from the suggestions.");
        speak("Please select a financial year.");
        return;
    }

    // STEP 3 — IS DEFAULT
    if (pending.IsDefault === undefined) {
        const isDefault = text.toLowerCase().includes("yes");

        setPending({ ...pending, IsDefault: isDefault });

        pushAIChat("Allow standard deduction? Yes or No?");
        speak("Allow standard deduction?");
        return;
    }

    // STEP 4 — ALLOW STANDARD DEDUCTION
    if (pending.AllowsStandardDeduction === undefined) {
        const allow = text.toLowerCase().includes("yes");

        setPending({ ...pending, AllowsStandardDeduction: allow });

        pushAIChat("Enter standard deduction amount.");
        speak("Enter standard deduction amount.");
        return;
    }

    // STEP 5 — STANDARD DEDUCTION AMOUNT
    if (pending.StandardDeductionAmount === undefined) {
        const amount = Number(text.replace(/[^0-9]/g, ""));

        setPending({ ...pending, StandardDeductionAmount: amount || 0 });

        pushAIChat("Allow Chapter VI-A deductions? Yes or No?");
        speak("Allow Chapter six A deductions?");
        return;
    }

    // STEP 6 — CHAPTER VI-A
    if (pending.AllowsChapterVI_ADeductions === undefined) {
        const allow = text.toLowerCase().includes("yes");

        setPending({ ...pending, AllowsChapterVI_ADeductions: allow });

        pushAIChat("Enter max income for 87A rebate.");
        speak("Enter max income for 87A rebate.");
        return;
    }

    // STEP 7 — REBATE MAX INCOME
    if (pending.RebateUnderSection87A_MaxIncome === undefined) {
        const income = Number(text.replace(/[^0-9]/g, ""));

        setPending({ ...pending, RebateUnderSection87A_MaxIncome: income || 0 });

        pushAIChat("Enter 87A rebate amount.");
        speak("Enter rebate amount.");
        return;
    }

    // STEP 8 — REBATE AMOUNT
    if (pending.RebateUnderSection87A_Amount === undefined) {
        const amount = Number(text.replace(/[^0-9]/g, ""));

        setPending({ ...pending, RebateUnderSection87A_Amount: amount || 0 });

        pushAIChat("Should this regime be active or inactive?");
        speak("Should this regime be active or inactive?");
        return;
    }

    // STEP 9 — STATUS (FINAL)
    if (!pending.Status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        pushAIChat(`Creating tax regime "${pending.RegimeName}"...`);
        speak("Creating tax regime.");

        window.dispatchEvent(
            new CustomEvent("ai-create-tax-regime", {
                detail: {
                    ...pending,
                    Status: status
                }
            })
        );

        setPending(null);
    }
};

// -------------------------------
// DELETE TAX REGIME — START
// -------------------------------
export const startDeleteTaxRegimeFlow = (
    setPending: (data: PendingDeleteTaxRegime | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting tax regime "${nameFromIntent}"...`);
        speak(`Deleting tax regime ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-tax-regime", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingName: true });
    pushAIChat("Which tax regime would you like to delete?");
    speak("Which tax regime would you like to delete?");
};

// -------------------------------
// DELETE TAX REGIME — PROCESS
// -------------------------------
export const processPendingDeleteTaxRegime = (
    text: string,
    pending: PendingDeleteTaxRegime,
    setPending: (data: PendingDeleteTaxRegime | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const name = text.trim();

    if (!name) {
        pushAIChat("Please provide a valid tax regime name.");
        speak("Invalid tax regime name.");
        return;
    }

    pushAIChat(`Deleting tax regime "${name}"...`);
    speak(`Deleting tax regime ${name}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-tax-regime", {
            detail: { name }
        })
    );

    setPending(null);
};

